import { get, post, put, del } from "@/api/client";
import type { TitleMap } from "@/api/content";

// Free-form label; the timeline is driven by available_offset_days (month/week/day picker).
export type Phase = string;
export type JourneyStatus = "draft" | "active" | "archived";

export interface Journey {
  id: string;
  tenant_id: string;
  name: string;
  name_i18n?: TitleMap;
  description: string | null;
  target_segment_id: string | null;
  status: JourneyStatus;
  created_at: string;
  updated_at: string;
}

export interface JourneyStep {
  id: string;
  journey_id: string;
  module_id: string;
  order_index: number;
  phase: Phase;
  required: boolean;
  available_offset_days: number;
  module_title: string;
  module_status: string;
  content_type: string | null;
}

export interface JourneyDetail extends Journey {
  steps: JourneyStep[];
}

export interface JourneyInput {
  name: string;
  name_i18n?: TitleMap;
  description?: string | null;
  target_segment_id?: string | null;
}

export interface AddStepInput {
  module_id: string;
  phase?: string;
  required: boolean;
  available_offset_days: number;
}

async function unwrap<T>(p: Promise<{ ok: true; data: T } | { ok: false; err: { message: string } }>): Promise<T> {
  const r = await p;
  if (!r.ok) throw new Error(r.err.message);
  return r.data;
}

export const listJourneys = (signal?: AbortSignal) => unwrap<Journey[]>(get("/api/journeys", signal));
export const getJourney = (id: string, signal?: AbortSignal) => unwrap<JourneyDetail>(get(`/api/journeys/${id}`, signal));
export const createJourney = (input: JourneyInput) => unwrap<Journey>(post("/api/journeys", input));
export const updateJourney = (id: string, input: JourneyInput) => unwrap<JourneyDetail>(put(`/api/journeys/${id}`, input));
export const setJourneyStatus = (id: string, status: JourneyStatus) =>
  unwrap<JourneyDetail>(post(`/api/journeys/${id}/status`, { status }));
export const addStep = (id: string, input: AddStepInput) => unwrap<JourneyDetail>(post(`/api/journeys/${id}/steps`, input));
export const updateStep = (id: string, stepId: string, input: Omit<AddStepInput, "module_id">) =>
  unwrap<JourneyDetail>(put(`/api/journeys/${id}/steps/${stepId}`, input));
export const deleteStep = (id: string, stepId: string) => unwrap<JourneyDetail>(del(`/api/journeys/${id}/steps/${stepId}`));
export const reorderSteps = (id: string, orderedStepIds: string[]) =>
  unwrap<JourneyDetail>(post(`/api/journeys/${id}/steps/reorder`, { ordered_step_ids: orderedStepIds }));
